import React, { useCallback, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getReports, resolveReport, type ReportAction, type ReportList, type ReportReason, type ReportStatus, type RiderReport,
} from './adminApi';
import { useAdminAccess } from './adminAccess';
import { usePolling } from './usePolling';
import { formatCount, formatUtc } from './format';
import { SlowBanner, Unavailable } from './PanelState';
import './reports.css';

const REASON_LABEL: Record<ReportReason, string> = {
  offensive_name: 'Offensive name',
  cheating: 'Cheating',
  other: 'Other',
};

const OUTCOME_LABEL: Record<NonNullable<RiderReport['action']>, string> = {
  dismiss: 'Dismissed',
  reset_name: 'Name reset',
  account_closed: 'Account closed',
};

const STATUSES: Array<{ value: ReportStatus; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'resolved', label: 'Resolved' },
];

/** How a player-reported rider is shown: the current name, and the reported one if it has changed since. */
const RiderName: React.FC<{ report: RiderReport }> = ({ report }) => (
  <>
    <span className="admin-report-name">{report.currentName}</span>
    {report.nameAtReport && report.nameAtReport !== report.currentName && (
      <span className="admin-muted admin-report-was">Reported as {report.nameAtReport}</span>
    )}
  </>
);

/**
 * Player reports about riders. Open reports are listed oldest first with two
 * decisions: Dismiss, or Reset name, which puts the rider back on its generated
 * name and closes the other open name reports about it. Every decision is
 * recorded server-side with the administrator's id.
 */
const ReportsPanel: React.FC = () => {
  const { token } = useAuth();
  const report = useAdminAccess();
  const [status, setStatus] = useState<ReportStatus>('open');
  const [working, setWorking] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [failure, setFailure] = useState('');

  const fetcher = useCallback((signal: AbortSignal) => getReports(token, status, signal), [token, status]);
  const { data, error, refresh } = usePolling<ReportList>(fetcher, null, { onError: report });

  const decide = async (item: RiderReport, action: ReportAction) => {
    setWorking(item.id); setNotice(''); setFailure('');
    const result = await resolveReport(token, item.id, action);
    setWorking(null);
    if (result.ok) {
      const others = result.data.resolved - 1;
      setNotice(action === 'reset_name'
        ? `${item.currentName} is back on a generated name.${others > 0 ? ` ${formatCount(others)} other ${others === 1 ? 'report was' : 'reports were'} closed too.` : ''}`
        : 'Report dismissed.');
    } else if (result.error.kind === 'signin' || result.error.kind === 'forbidden') {
      report(result.error);
      return;
    } else if (result.error.kind === 'conflict' || result.error.kind === 'notfound') {
      setFailure('Someone has already dealt with that report. The list has been refreshed.');
    } else if (result.error.kind === 'slow') {
      setFailure('Too many requests. Wait a minute and try again.');
    } else {
      setFailure('Could not save that decision. Try again.');
    }
    refresh();
  };

  const showStatus = (next: ReportStatus) => { setStatus(next); setNotice(''); setFailure(''); };
  const items = data?.items ?? [];

  return (
    <div className="admin-reports">
      <div className="admin-toolbar">
        <label className="admin-field">
          <span>Show</span>
          <select value={status} onChange={event => showStatus(event.target.value as ReportStatus)}>
            {STATUSES.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <button type="button" className="admin-button" onClick={refresh}>Refresh</button>
      </div>

      {notice && <p className="admin-banner admin-banner-ok" role="status">{notice}</p>}
      {failure && <p className="admin-banner admin-banner-error" role="alert">{failure}</p>}
      <SlowBanner error={error} />
      {error && error.kind !== 'signin' && error.kind !== 'forbidden' && error.kind !== 'slow' && (
        <Unavailable message="Reports unavailable" onRetry={refresh} />
      )}
      {!data && !error && <p className="admin-muted">Loading reports…</p>}

      {data && !error && (items.length === 0
        ? <p className="admin-empty">{status === 'open' ? 'No open reports.' : 'No resolved reports yet.'}</p>
        : (
          <div className="admin-table-wrap">
            <table className="admin-table admin-reports-table">
              <caption className="sr-only">{status === 'open' ? 'Open reports, oldest first' : 'Resolved reports, newest first'}</caption>
              <thead>
                <tr>
                  <th scope="col">Reported</th>
                  <th scope="col">Rider</th>
                  <th scope="col">Reason</th>
                  <th scope="col">Note</th>
                  {status === 'open'
                    ? <><th scope="col" className="num">Open</th><th scope="col">Decision</th></>
                    : <><th scope="col">Outcome</th><th scope="col">Resolved</th></>}
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id}>
                    <td>{formatUtc(item.createdAt)}</td>
                    <td><RiderName report={item} /></td>
                    <td>{REASON_LABEL[item.reason] ?? item.reason}</td>
                    <td className="admin-report-note">{item.note || <span className="admin-muted">—</span>}</td>
                    {status === 'open' ? (
                      <>
                        <td className="num">{formatCount(item.openReports)}</td>
                        <td>
                          <div className="admin-row-actions">
                            <button type="button" className="admin-button" disabled={working !== null}
                              onClick={() => decide(item, 'dismiss')}
                              aria-label={`Dismiss report about ${item.currentName}`}>Dismiss</button>
                            <button type="button" className="admin-button admin-button-danger" disabled={working !== null || !item.hasCustomName}
                              title={item.hasCustomName ? undefined : 'Already on a generated name'}
                              onClick={() => decide(item, 'reset_name')}
                              aria-label={`Reset name of ${item.currentName}`}>{working === item.id ? 'Saving…' : 'Reset name'}</button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{item.action ? OUTCOME_LABEL[item.action] : '—'}</td>
                        <td>{formatUtc(item.resolvedAt)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </div>
  );
};

export default ReportsPanel;
